import * as THREE from 'three';
import html2canvas from 'html2canvas';
import { ViewPortResizer } from './viewPortResizer';

import type {
  IViewPortOrientation,
  ICameraController,
  IIlluminator,
  IResizeHandler,
  IViewPortController,
  ITwoDLayerController,
  IViewPortManipulationApi,
  ICameraInfoApi,
  IContextStateApi,
  Intersection,
  IRaycastable,
  IViewPortControllerStrategy,
  Location
} from '@/engine/app/interfaces';
import { AutoEventSubscription } from '@/engine/Utilities/AutoEventSubscription';
import { CameraController } from './cameraController';
import { Illuminator } from './illuminator';
import { CallbackManager } from '@/engine/Utilities/CallbackManager';
import { AutoDisposer } from '@/engine/Utilities/AutoDisposer';
import { Vector2 } from 'three';

import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';
import type { ITwoDViewModel, IWeightedGravityCenter } from '@/engine/UIPresentation/Interfaces';
import type { ViewPortTooltipViewModel } from '@/engine/app/models/ViewPortTooltipViewModel';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import type { ContextEvents } from '@/engine/app/modules/images/interfaces.ts';
import { statusCursor } from '@/assets/cursors/urlCursors';

interface _EventData {
  subscription: IDisposable;
  listeners: Set<EventListener>;
}

class _EventTargetProxy {
  private _target: EventTarget;

  private readonly _eventsSusbsciptions = new Map<string, _EventData>();

  public constructor(target: EventTarget) {
    this._target = target;
  }

  private _subscribe(type: string, listeners: Set<EventListener>): IDisposable {
    return new AutoEventSubscription(this._target, type, (event: Event) => {
      listeners.forEach((listener) => listener(event));
    });
  }

  public updateTarget(target: EventTarget): void {
    this._target = target;
    for (const [type, data] of this._eventsSusbsciptions) {
      data.subscription.dispose();
      data.subscription = this._subscribe(type, data.listeners);
    }
  }

  public addEventListener(type: string, callback: EventListener): IDisposable {
    let data = this._eventsSusbsciptions.get(type);
    if (!data) {
      const listeners = new Set<EventListener>();
      const subcription = this._subscribe(type, listeners);
      data = {
        subscription: subcription,
        listeners: listeners
      };
      this._eventsSusbsciptions.set(type, data);
    }
    data.listeners.add(callback);
    return {
      dispose: (): void => {
        data.listeners.delete(callback);
        if (data.listeners.size == 0) {
          data.subscription.dispose();
          this._eventsSusbsciptions.delete(type);
        }
      }
    };
  }

  public dispatchEvent(event: Event): boolean {
    return this._target.dispatchEvent(event);
  }
}

export class ViewPortController implements IViewPortController, IResizeHandler {
  private readonly _scene: THREE.Scene;

  private _clippingPlanes: Array<THREE.Plane> = [];

  private readonly _clippableObjects = new Set<THREE.Object3D>();

  private _renderer: THREE.WebGLRenderer;

  private _resizer: ViewPortResizer;

  private _canvas: HTMLCanvasElement;

  private readonly _eventTargetProxy: _EventTargetProxy;

  private readonly _cameraController: ICameraController;

  private readonly _illuminator: IIlluminator;

  private _isMaximized = false;

  private _viewPortManipulationApi?: IViewPortManipulationApi;

  private _contextStateApi?: IContextStateApi;

  private _strategy?: IViewPortControllerStrategy;

  private readonly _twoDLayerController: ITwoDLayerController;

  private readonly _maximizedChangedListeners = new CallbackManager<(isMaximized: boolean) => void>();

  private _bgColor: number;

  private readonly _cameraSubscription: IDisposable;

  private readonly _fitObjects = new Set<THREE.Mesh>();

  private readonly _weightedGravityCenters = new Map<THREE.Object3D, IWeightedGravityCenter>();

  private _renderingRequested = false;

  private _isRenderingAllowed = true;

  public constructor(
    canvas: HTMLCanvasElement,
    canvasContainer: HTMLDivElement,
    renderer: THREE.WebGLRenderer,
    bgColor: number,
    twoDLayerController: ITwoDLayerController
  ) {
    this._canvas = canvas;
    this._eventTargetProxy = new _EventTargetProxy(canvasContainer);
    this._illuminator = new Illuminator();
    this._cameraController = new CameraController(this._canvas);
    this._cameraSubscription = this._cameraController.onCameraModified((cameraInfo: ICameraInfoApi): void => {
      const topVector = cameraInfo.getCameraTop();
      const directionVector = cameraInfo.getCameraDirection();
      this._illuminator.setOrientation(topVector, directionVector);
    });
    this._twoDLayerController = twoDLayerController;
    this._bgColor = bgColor;
    this._scene = ViewPortController._createScene(this._illuminator);
    this._renderer = renderer;
    this._renderer.setClearColor(this._bgColor, 1);
    this._renderer.localClippingEnabled = true;

    this._resizer = new ViewPortResizer(
      this._renderer.domElement.parentElement!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
      this
    );
    this.setCursor(statusCursor);
  }

  public dispose(): void {
    this._resizer.dispose();
    this._twoDLayerController.dispose();
    this._cameraController.dispose();
    this._cameraSubscription.dispose();
    this._viewPortManipulationApi?.setViewPortController(undefined);
    this._viewPortManipulationApi = undefined;
    this._contextStateApi = undefined;
    this._strategy = undefined;
  }

  public reInitialize(canvas: HTMLCanvasElement, canvasContainer: HTMLDivElement, renderer: THREE.WebGLRenderer): void {
    this._canvas = canvas;

    this._cameraController.updateCanvas(this._canvas);

    this._eventTargetProxy.updateTarget(canvasContainer);

    this._renderer = renderer;
    this._renderer.setClearColor(this._bgColor, 1);
    this._renderer.localClippingEnabled = true;

    this._resizer.dispose();
    this._resizer = new ViewPortResizer(
      this._renderer.domElement.parentElement!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
      this
    );
  }

  public getTwoDLayerController(): ITwoDLayerController {
    return this._twoDLayerController;
  }

  public getCommonCenterPoint(): THREE.Vector3 {
    const vector = new THREE.Vector3();
    let weight = 0;
    for (const [obj, pt] of this._weightedGravityCenters) {
      if (obj.visible) {
        vector.add(pt.vector);
        weight += pt.weight;
      }
    }
    if (weight > 0) {
      return vector.divideScalar(weight);
    } else {
      return this._cameraController.getFitCenterPoint();
    }
  }

  public setClippingPlanes(planes: Array<THREE.Plane>): void {
    this._clippingPlanes = planes;
    this._clippableObjects.forEach((object) =>
      object.traverse((obj) => {
        const withMaterial = obj as unknown as { material?: THREE.Material };
        if (withMaterial.material) {
          withMaterial.material.clippingPlanes = this._clippingPlanes;
        }
      })
    );
    this.render();
  }

  public rayCast(raycastables: Set<IRaycastable>, point: THREE.Vector2): Array<Intersection> {
    const ray = this._cameraController.getViewRay(point);
    const doNotOptimize = this._clippingPlanes.length > 0;
    const intersections: Array<Intersection> = [];
    for (const entry of raycastables) {
      intersections.push(...entry.rayCast(ray.origin, ray.direction, doNotOptimize));
    }
    intersections.sort((a: Intersection, b: Intersection): number => {
      return a.distance - b.distance;
    });
    if (this._clippingPlanes.length == 0) {
      return intersections;
    }
    return intersections.filter((value: Intersection): boolean => {
      if (value.isClippable === false) {
        return true;
      }
      return this._clippingPlanes.every((plane: THREE.Plane): boolean => plane.distanceToPoint(value.point) > 0);
    });
  }

  public setViewPortManipulationApi(viewPortManipulationApi?: IViewPortManipulationApi): void {
    this._viewPortManipulationApi = viewPortManipulationApi;
    this._viewPortManipulationApi?.setViewPortController(this);
  }

  public setContextStateApi(contextStateApi?: IContextStateApi): void {
    this._contextStateApi = contextStateApi;
  }

  public setStrategy(strategy: IViewPortControllerStrategy): void {
    this._strategy = strategy;
  }

  public setViewRoi(location: Location, zoomFactor: number): void {
    if (!this._strategy) {
      throw new Error('Camera Strategy is not set');
    }
    this._strategy.setViewRoi(location, zoomFactor);
  }

  public getCameraController(): ICameraController {
    return this._cameraController;
  }

  public getSize(): Vector2 {
    return this._renderer.getSize(new Vector2());
  }

  public setBackgroundColor(bgColor: number): void {
    this._bgColor = bgColor;
    this._renderer.setClearColor(bgColor);
  }

  public setMaximized(isMaximized: boolean): void {
    this._isMaximized = isMaximized;
    this._maximizedChangedListeners.invoke(isMaximized);
  }

  public isMaximized(): boolean {
    return this._isMaximized;
  }

  public trackMaximizedChanged(callback: (isMaximized: boolean) => void): CallbackSubscription {
    return this._maximizedChangedListeners.subscribe(callback);
  }

  public addSceneObject(
    object: THREE.Object3D,
    usedInFit: boolean,
    canBeClipped = true,
    weightedGravityCenter?: IWeightedGravityCenter
  ): void {
    this.render();

    if (weightedGravityCenter) {
      this._weightedGravityCenters.set(object, weightedGravityCenter);
    }

    if (object instanceof THREE.Mesh && usedInFit) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this._fitObjects.add(object);
    }
    if (canBeClipped) {
      this._clippableObjects.add(object);
    }
    this._scene.add(object);
  }

  public addTwoDObject(twoDViewModel: ITwoDViewModel): void {
    this._twoDLayerController.addViewModel(twoDViewModel);
  }

  public removeTwoDObject(twoDViewModel: ITwoDViewModel): void {
    this._twoDLayerController.removeViewModel(twoDViewModel);
  }

  public addTooltip(tooltip: ViewPortTooltipViewModel): void {
    this._twoDLayerController.addTooltip(tooltip);
  }

  public removeTooltip(tooltip: ViewPortTooltipViewModel): void {
    this._twoDLayerController.removeTooltip(tooltip);
  }

  public removeSceneObject(object: THREE.Object3D): void {
    if (object instanceof THREE.Mesh) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this._fitObjects.delete(object);
    }
    this._weightedGravityCenters.delete(object);
    this._scene.remove(object);
    this._clippableObjects.delete(object);
    this.render();
  }

  public setupCamera(orientation: IViewPortOrientation): void {
    this._cameraController.setCameraOrientation(orientation);
  }

  public getFitObjects(): Array<THREE.Mesh> {
    return Array.from(this._fitObjects);
  }

  public fitCameraToObjects(fittingFactor?: number, objectsToFit?: Array<THREE.Mesh>): void {
    this._cameraController.fitCameraToObjects(objectsToFit || this.getFitObjects(), {
      fittingFactor: fittingFactor ?? 0.95
    });
  }

  public synchronizeCamera(anotherCamera: ICameraController): void {
    if (!this._strategy) {
      throw new Error('Camera Strategy is not set');
    }
    this._strategy.synchronizeCamera(anotherCamera);
  }

  public setNoRotateCamera(noRotate: boolean): void {
    if (!this._strategy) {
      throw new Error('Camera Strategy is not set');
    }
    this._strategy.setNoRotateCamera(noRotate);
  }

  public addEventListener(type: string, listener: EventListener): IDisposable {
    return this._eventTargetProxy.addEventListener(type, listener);
  }

  public addKeyboardEventListener(type: string, listener: EventListener): IDisposable {
    return new AutoEventSubscription(document, type, listener);
  }

  public setPointerCapture(pointerId: number): void {
    this._canvas.setPointerCapture(pointerId);
  }

  public releasePointerCapture(pointerId: number): void {
    this._canvas.releasePointerCapture(pointerId);
  }

  public handleViewPortResize = (width: number, height: number): void => {
    this._renderer.setSize(width, height);
    this._cameraController.handleResize();
    this.render();
  };

  public resize(): void {
    this._resizer.resize();
  }

  /**
   * Sets the size of the viewport explicitly.
   *
   * @param width - The width of the viewport.
   * @param height - The height of the viewport.
   */
  public setSizeExplicitly(width: number, height: number): void {
    this._resizer.setSizeExplicitly(width, height);
  }

  /**
   * Resets the size of the viewport to its original size.
   */
  public resetSize(): void {
    this._resizer.resetSize();
  }

  /**
   * Resets the view of the viewport.
   */
  public resetView(): void {
    this._strategy?.resetView();
  }

  public render(): void {
    this._renderingRequested = this._isRenderingAllowed; // [kekalo]: debounce bulk rendering requests
    requestAnimationFrame(() => {
      if (this._renderingRequested) {
        this._cameraController.accessCamera((camera) => {
          // clear
          this._renderer.clear();
          this._renderer.render(this._scene, camera);
        });
        this._renderingRequested = false;
      }
    });
  }

  public acquireRenderingLock(): IDisposable {
    this._isRenderingAllowed = false;
    return new AutoDisposer(() => {
      this._isRenderingAllowed = true;
      this.render();
    });
  }

  public setCursor(cursor: string): void {
    this._canvas.style.cursor = cursor && cursor !== 'default' ? cursor : statusCursor;
  }

  public triggerEvent(name: string, data: number): void {
    const event = new CustomEvent(name, { detail: data });
    this._eventTargetProxy.dispatchEvent(event);
  }

  public setContextMenuState(state: ContextEvents): void {
    if (this._contextStateApi) {
      this._contextStateApi.setContextState(state);
    }
  }

  public get width(): number {
    return this._canvas.clientWidth;
  }

  public get height(): number {
    return this._canvas.clientHeight;
  }

  public async getScreenshot(): Promise<string> {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const canvasContainer = this._renderer.domElement.parentElement!;
    return html2canvas(canvasContainer).then((canvas: HTMLCanvasElement) => {
      return canvas.toDataURL('image/png');
    });
  }

  private static _createScene(illuminator: IIlluminator): THREE.Scene {
    const scene = new THREE.Scene();
    scene.add(illuminator.getLight());
    scene.add(illuminator.getAmbientLight());
    return scene;
  }
}
